import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Trash2, ChevronRight, ChevronDown, ChevronLeft,
} from "lucide-react";

const SOURCE_LABELS = { role_based: "Mock", custom_role: "Custom", job: "JD" };
const SOURCE_COLORS = {
  role_based: "bg-[#0025D8] text-white",
  custom_role: "bg-[#7C3AED] text-white",
  job: "bg-[#0F172A] text-white",
};
const ROUND_COLORS = { hr: "bg-[#0025D8]", technical: "bg-[#0F172A]", behavioral: "bg-[#7C3AED]" };

function Stat({ label, value }) {
  return (
    <div className="bg-white border-r border-b border-border p-5">
      <div className="font-mono text-[9px] text-muted-foreground tracking-[0.25em] uppercase">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

export default function AdminCandidates() {
  const [interviews, setInterviews] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterRoleId, setFilterRoleId] = useState("all");
  const [expandedCandidates, setExpandedCandidates] = useState(new Set());

  const toggle = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  useEffect(() => {
    (async () => {
      try {
        const [iv, rl] = await Promise.all([
          api.get("/admin/interviews"),
          api.get("/admin/roles"),
        ]);
        setInterviews(iv.data);
        setRoles(rl.data);
      } catch {
        toast.error("Could not load candidates data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const deleteIv = async (id) => {
    if (!window.confirm("Delete this interview permanently?")) return;
    try {
      await api.delete(`/admin/interviews/${id}`);
      setInterviews((arr) => arr.filter((i) => i.interview_id !== id));
      toast.success("Interview deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>;

  const filteredInterviews = filterRoleId === "all"
    ? interviews
    : filterRoleId === "custom"
    ? interviews.filter((iv) => iv.source === "custom_role")
    : interviews.filter((iv) => iv.role_id === filterRoleId);

  const candidateGroups = Object.values(
    filteredInterviews.reduce((acc, iv) => {
      const key = iv.candidate_email;
      if (!acc[key]) acc[key] = { name: iv.candidate_name, email: iv.candidate_email, interviews: [] };
      acc[key].interviews.push(iv);
      return acc;
    }, {})
  );

  const completed = interviews.filter((i) => i.status === "completed").length;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground mb-6">
        <Link to="/admin" className="hover:text-foreground flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Admin
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground">Interviews</span>
      </div>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Candidates · Control</div>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Interviews</h1>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 border-l border-t border-border mb-10 max-w-md">
        <Stat label="Interviews" value={interviews.length} />
        <Stat label="Completed" value={completed} />
      </div>

      {/* ── Interviews section ── */}
      <section>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-display font-bold text-2xl">All candidate interviews</h2>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">
              {filteredInterviews.length} interview{filteredInterviews.length !== 1 ? "s" : ""} · {candidateGroups.length} candidate{candidateGroups.length !== 1 ? "s" : ""}
            </p>
          </div>
          <select
            value={filterRoleId}
            onChange={(e) => setFilterRoleId(e.target.value)}
            className="rounded-xl border border-border bg-background px-3 h-9 text-xs font-mono rounded-xl focus:outline-none focus:ring-1 focus:ring-[#0025D8] min-w-[200px]"
          >
            <option value="all">All roles ({interviews.length})</option>
            <option value="custom">Custom roles ({interviews.filter((iv) => iv.source === "custom_role").length})</option>
            <option disabled>──────────────────</option>
            {roles
              .map((r) => ({ ...r, count: interviews.filter((iv) => iv.role_id === r.role_id).length }))
              .filter((r) => r.count > 0)
              .sort((a, b) => b.count - a.count)
              .map((r) => (
                <option key={r.role_id} value={r.role_id}>{r.name} ({r.count})</option>
              ))}
          </select>
        </div>

        {candidateGroups.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center text-muted-foreground">No interviews found.</div>
        ) : (
          <div className="border-l border-t border-border">
            {candidateGroups.map((group) => {
              const isExpanded = expandedCandidates.has(group.email);
              return (
                <div key={group.email}>
                  <button
                    className="w-full grid grid-cols-12 border-r border-b border-border items-center hover:bg-secondary/40 transition-colors"
                    onClick={() => toggle(setExpandedCandidates)(group.email)}
                  >
                    <div className="col-span-1 p-4 border-r border-border flex items-center justify-center">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <div className="col-span-9 p-4 border-r border-border flex items-center gap-3 text-left">
                      <div className="w-8 h-8 bg-[#0025D8] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {group.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{group.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{group.email}</div>
                      </div>
                    </div>
                    <div className="col-span-2 p-4 flex items-center justify-end pr-5">
                      <span className="font-mono text-[10px] rounded-xl border border-border px-2 py-0.5 text-muted-foreground">
                        {group.interviews.length} interview{group.interviews.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="border-b border-border">
                      <div className="grid grid-cols-12 border-b border-border bg-[#0025D8]/5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        <div className="col-span-1 border-r border-border" />
                        <div className="col-span-5 p-3 border-r border-border">Role · Round · Type</div>
                        <div className="col-span-3 p-3 border-r border-border">Score</div>
                        <div className="col-span-1 p-3 border-r border-border">Cheats</div>
                        <div className="col-span-1 p-3 border-r border-border">Date</div>
                        <div className="col-span-1 p-3" />
                      </div>
                      {group.interviews.map((iv) => {
                        const score = iv.overall_score ? Math.round(iv.overall_score) : null;
                        const sc = score == null ? "" : score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#E11D48";
                        const cheats = iv.cheat_events?.length || 0;
                        return (
                          <div key={iv.interview_id} className="grid grid-cols-12 border-b border-border items-center bg-white hover:bg-secondary/20 transition-colors">
                            <div className="col-span-1 border-r border-border self-stretch border-l-2 border-l-[#0025D8]/30" />
                            <div className="col-span-5 p-3 border-r border-border min-w-0">
                              <div className="text-sm font-medium truncate">{iv.job_title}</div>
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                <span className={`px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase ${SOURCE_COLORS[iv.source] || "bg-secondary text-muted-foreground"}`}>
                                  {SOURCE_LABELS[iv.source] || "JD"}
                                </span>
                                {iv.round && (
                                  <span className={`px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase text-white ${ROUND_COLORS[iv.round] || "bg-secondary"}`}>
                                    {iv.round}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="col-span-3 p-3 border-r border-border">
                              {score != null ? (
                                <div>
                                  <span className="font-mono font-bold text-sm" style={{ color: sc }}>{score}</span>
                                  <div className="mt-1 h-1 w-full bg-border overflow-hidden">
                                    <div className="h-1 transition-all" style={{ width: `${score}%`, backgroundColor: sc }} />
                                  </div>
                                </div>
                              ) : <span className="font-mono text-xs text-muted-foreground">—</span>}
                            </div>
                            <div className={`col-span-1 p-3 border-r border-border font-mono text-sm text-center ${cheats > 0 ? "text-[#E11D48] font-bold" : "text-muted-foreground"}`}>
                              {cheats > 0 ? `${cheats} ⚠` : "0"}
                            </div>
                            <div className="col-span-1 p-3 border-r border-border font-mono text-[10px] text-muted-foreground">
                              {new Date(iv.created_at).toLocaleDateString("en", { month: "short", day: "numeric" })}
                            </div>
                            <div className="col-span-1 p-3 flex gap-1.5 items-center justify-center">
                              <Link to={`/interviews/${iv.interview_id}/results`} className="text-[#0025D8] text-sm font-bold hover:underline" title="View results">→</Link>
                              <Button variant="outline" size="icon" onClick={() => deleteIv(iv.interview_id)} className="rounded-xl h-7 w-7 text-[#E11D48] hover:bg-[#E11D48] hover:text-white">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
